import { useCopilotAction } from '@copilotkit/react-core';

import { LumeoEvent, LumeoEventSeverity } from 'types/event';

import { useAPI } from 'hooks/api/useAPI';

export function useListEventsTool() {
  const { applicationID, client } = useAPI();

  useCopilotAction({
    name: 'listEvents',
    description:
      'List system events in the system. Use this to find events related to cameras, gateways, streams, or deployments. Can filter by object IDs, event types, severities, and time range. Use individual listXXX tools to find names/details from returned Gateway, Camera, Stream, or Deployment IDs.',
    parameters: [
      {
        name: 'gateway_ids',
        type: 'string',
        description: 'JSON array of gateway IDs to filter events by',
        required: false,
      },
      {
        name: 'camera_ids',
        type: 'string',
        description: 'JSON array of camera IDs to filter events by',
        required: false,
      },
      {
        name: 'stream_ids',
        type: 'string',
        description: 'JSON array of stream IDs to filter events by',
        required: false,
      },
      {
        name: 'deployment_ids',
        type: 'string',
        description: 'JSON array of deployment IDs to filter events by',
        required: false,
      },
      {
        name: 'event_types',
        type: 'string',
        description:
          'JSON array of event types to filter by, e.g. ["camera.online", "camera.offline", "deployment.started", "deployment.stopped", "deployment.error", "gateway.online", "gateway.offline", "stream.online", "stream.offline"]',
        required: false,
      },
      {
        name: 'severities',
        type: 'string',
        description:
          'JSON array of severities to filter by: ["error", "warning", "info", "debug"]',
        required: false,
      },
      {
        name: 'since',
        type: 'string',
        description:
          'ISO 8601 timestamp to filter events from (inclusive). Example: "2024-01-01T00:00:00Z"',
        required: false,
      },
      {
        name: 'until',
        type: 'string',
        description:
          'ISO 8601 timestamp to filter events until (inclusive). Example: "2024-01-31T23:59:59Z"',
        required: false,
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum number of results to return. Defaults to 50.',
        required: false,
      },
    ],
    handler: async (args: {
      gateway_ids?: string | string[];
      camera_ids?: string | string[];
      stream_ids?: string | string[];
      deployment_ids?: string | string[];
      event_types?: string | string[];
      severities?: string | LumeoEventSeverity[];
      since?: string;
      until?: string;
      limit?: number;
    }) => {
      const parseArrayParam = (
        param: string | unknown[] | undefined
      ): unknown[] | undefined => {
        if (!param) return undefined;
        if (Array.isArray(param)) return param;
        try {
          return JSON.parse(param);
        } catch {
          // If it's not valid JSON, treat it as a single value
          return [param];
        }
      };

      try {

        const gatewayIds = parseArrayParam(args.gateway_ids) as string[] | undefined;
        const cameraIds = parseArrayParam(args.camera_ids) as string[] | undefined;
        const streamIds = parseArrayParam(args.stream_ids) as string[] | undefined;
        const deploymentIds = parseArrayParam(args.deployment_ids) as string[] | undefined;
        const eventTypes = parseArrayParam(args.event_types) as string[] | undefined;
        const severities = parseArrayParam(args.severities) as LumeoEventSeverity[] | undefined;

        // Combine all object IDs
        const objectIds = [
          ...(gatewayIds || []),
          ...(cameraIds || []),
          ...(streamIds || []),
          ...(deploymentIds || []),
        ];

        // Determine object types based on which IDs were provided
        const objectTypes: string[] = [];
        if (gatewayIds?.length) objectTypes.push('gateway');
        if (cameraIds?.length) objectTypes.push('camera');
        if (streamIds?.length) objectTypes.push('stream');
        if (deploymentIds?.length) objectTypes.push('deployment');

        const { data } = await client.get(`/apps/${applicationID}/events`, {
          params: {
            pagination: 'offset',
            page: 1,
            limit: args.limit || 50,
            object_ids: objectIds.length > 0 ? objectIds : undefined,
            object_types: objectTypes.length > 0 ? objectTypes : undefined,
            event_types: eventTypes,
            severities,
            since: args.since,
            until: args.until,
          },
        });

        const events = data?.data || [];

        if (events.length === 0) {
          return {
            message: 'No events found matching your criteria.',
            events: [],
          };
        }

        return {
          message: `Found ${events.length} event(s)`,
          events: events.map((event: LumeoEvent) => ({
            id: event.id,
            event_ts: event.event_ts,
            category: event.category,
            event_type: event.event_type,
            severity: event.severity,
            payload: event.payload,
            related_entities: event.related_entities,
          })),
        };
      } catch (error) {
        return {
          message: 'Failed to load events',
          error: error instanceof Error ? error.message : 'Unknown error',
          events: [],
        };
      }
    },
  });
}
